import { useState, useEffect, useMemo } from 'react';
import { 
  formatDateForForm, 
  formatTimeForForm, 
  formatDisplayDate, 
  getRelativeTime, 
  isToday, 
  isFuture,
  getWeekStart,
  getWeekEnd
} from '../utils/dateUtils';

interface UseDateTimeOptions {
  date?: Date;
  time?: string;
  showTime?: boolean;
  includeRelative?: boolean;
}

interface UseDateTimeReturn {
  formattedDate: string;
  formattedTime: string;
  displayDate: string;
  relativeTime: string;
  isTodayDate: boolean;
  isFutureDate: boolean;
  weekStart: Date;
  weekEnd: Date;
  formDate: string;
  formTime: string;
}

/**
 * Hook for managing date and time formatting and manipulation
 * Useful for: Forms, displays, scheduling, any date/time-related UI
 */
export const useDateTime = (options: UseDateTimeOptions): UseDateTimeReturn => {
  const { date, time, showTime = false, includeRelative = true } = options;

  const formattedDate = useMemo(() => {
    if (!date) return "";
    return formatDateForForm(date, new Date());
  }, [date]);

  const formattedTime = useMemo(() => {
    if (!date) return "";
    return formatTimeForForm(date, new Date());
  }, [date]);

  const displayDate = useMemo(() => {
    if (!date) return "";
    return formatDisplayDate(date, showTime);
  }, [date, showTime]);

  const relativeTime = useMemo(() => {
    if (!date || !includeRelative) return "";
    return getRelativeTime(date);
  }, [date, includeRelative]);

  const isTodayDate = useMemo(() => {
    if (!date) return false;
    return isToday(date);
  }, [date]);

  const isFutureDate = useMemo(() => {
    if (!date) return false;
    return isFuture(date);
  }, [date]);

  const weekStart = useMemo(() => {
    return getWeekStart(date);
  }, [date]);

  const weekEnd = useMemo(() => {
    return getWeekEnd(date);
  }, [date]);

  const formDate = useMemo(() => {
    if (!date) return "";
    return formatDateForForm(date, new Date());
  }, [date]);

  const formTime = useMemo(() => {
    if (!date) return "";
    return formatTimeForForm(date, new Date());
  }, [date]);

  return {
    formattedDate,
    formattedTime,
    displayDate,
    relativeTime,
    isTodayDate,
    isFutureDate,
    weekStart,
    weekEnd,
    formDate,
    formTime,
  };
};
